import { useEffect, useState } from "react";
import type { MatchmakingController } from "../hooks/useMatchmaking";
import type { MatchmakingMode } from "../game/api";
import { BrandMark } from "./BrandMark";

interface MatchmakingScreenProps {
  controller: MatchmakingController;
  sessionStatus: "bootstrapping" | "ready" | "unavailable";
  sessionError: string | null;
  supportedModes: MatchmakingMode[];
  onBack: () => void;
  onPrivate: () => void;
  onPractice: () => void;
}

export function MatchmakingScreen({
  controller,
  sessionStatus,
  sessionError,
  supportedModes,
  onBack,
  onPrivate,
  onPractice,
}: MatchmakingScreenProps) {
  const [mode, setMode] = useState<MatchmakingMode>(supportedModes[0] ?? "casual");
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const isSearching = controller.phase === "starting" || controller.phase === "searching";

  useEffect(() => {
    if (!isSearching) {
      setElapsedSeconds(0);
      return;
    }
    const startedAt = Date.parse(controller.ticket?.createdAt ?? "") || Date.now();
    const update = () => setElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1_000)));
    update();
    const timer = window.setInterval(update, 1_000);
    return () => window.clearInterval(timer);
  }, [controller.ticket?.createdAt, isSearching]);

  return (
    <main className="centered-screen queue-screen">
      <button className="back-button" type="button" onClick={onBack}>← Home</button>
      <BrandMark compact />
      <section className="dialog-card queue-card" aria-labelledby="queue-title">
        <p className="eyebrow"><span /> Public matchmaking</p>
        <h1 id="queue-title">Find your rival</h1>

        {isSearching ? (
          <>
            <div className="matchmaking-orb" aria-hidden="true"><span /><span /><span /><i>1v1</i></div>
            <p className="queue-status" role="status">
              {controller.phase === "starting" ? "Entering the public queue…" : "Searching for a compatible player…"}
            </p>
            <dl className="connection-facts">
              <div><dt>Mode</dt><dd>{controller.selectedMode}</dd></div>
              <div><dt>Region</dt><dd>{controller.ticket?.region ?? "AUTO"}</dd></div>
              <div><dt>Elapsed</dt><dd>{formatElapsed(elapsedSeconds)}</dd></div>
            </dl>
            {(controller.ticket?.position !== null || controller.ticket?.estimatedWaitSeconds !== null) && (
              <p className="availability-note queue-estimate">
                {controller.ticket?.position !== null && controller.ticket?.position !== undefined
                  ? `Queue position ${controller.ticket.position}. `
                  : ""}
                {controller.ticket?.estimatedWaitSeconds !== null && controller.ticket?.estimatedWaitSeconds !== undefined
                  ? `Estimated wait ${controller.ticket.estimatedWaitSeconds}s.`
                  : ""}
              </p>
            )}
            <button className="danger-button" type="button" onClick={() => void controller.cancel()}>Cancel search</button>
          </>
        ) : controller.phase === "error" ? (
          <>
            <div className="matchmaking-orb matchmaking-orb--error" aria-hidden="true"><span /><i>!</i></div>
            <p className="queue-status" role="alert">{controller.error ?? "Public matchmaking is unavailable."}</p>
            <div className="queue-actions">
              <button className="primary-button" type="button" onClick={controller.retry} disabled={sessionStatus !== "ready"}>Try again</button>
              <button className="secondary-button" type="button" onClick={onPrivate}>Use a room code</button>
              <button className="text-button" type="button" onClick={onPractice}>Practice locally</button>
            </div>
          </>
        ) : (
          <>
            <p className="queue-intro">Choose a real queue. A match starts only after the service pairs two authenticated players.</p>
            <div className="queue-modes" aria-label="Matchmaking mode">
              {supportedModes.map((candidate) => (
                <button
                  key={candidate}
                  className="queue-mode"
                  type="button"
                  aria-pressed={mode === candidate}
                  onClick={() => setMode(candidate)}
                >
                  <strong>{candidate === "casual" ? "Casual" : "Ranked"}</strong>
                  <small>{candidate === "casual" ? "Play without season rating stakes" : "Play for the current season rating"}</small>
                </button>
              ))}
            </div>
            {sessionStatus === "bootstrapping" && <p className="queue-status" role="status">Preparing a private device session…</p>}
            {sessionStatus === "unavailable" && <p className="queue-status" role="alert">{sessionError ?? "Online services are unavailable."}</p>}
            <button
              className="primary-button"
              type="button"
              disabled={sessionStatus !== "ready" || supportedModes.length === 0}
              onClick={() => controller.start(mode)}
            >
              Search {mode}
              <span aria-hidden="true">⌁</span>
            </button>
            {sessionStatus === "unavailable" && (
              <div className="queue-fallbacks">
                <button className="secondary-button" type="button" onClick={onPrivate}>Private match</button>
                <button className="secondary-button" type="button" onClick={onPractice}>Local practice</button>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}

function formatElapsed(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}
